import type {
  EvaluationScenario,
  ScenarioCheck,
  ScenarioControl,
  ScenarioEvent,
  ScenarioRelation,
  ScenarioSource,
  ScenarioToolDouble,
  WorldId,
} from '@/api/scenarioVersions';
import { CONTROL_WORDS, DOUBLE_OUTCOME_WORDS, RELATION_WORDS, checkPlace, checkSentence } from './scenarioWords';

// ─────────────────────────────────────────────────────────────────────────────
// Three-way merge of scenario versions (screen 10, frame D4; the same rules as the module editor's mergeModule, MOD-H1).
// Original is the version the editor opened; Yours is the edit; Current is the version someone saved meanwhile.
// Per field: a field you did not touch takes Current; a field only you changed takes Yours; the same change on both
// sides is no conflict; anything else is a conflict that needs a choice. Text fields may also be merged by hand.
// The merged result is a new edit on top of Current, and the server saves it only if it compiles.
// Contract version 3's parts merge the same way: the hazard and the named world as text, and each source, tool answer,
// sibling and control as one field of its own (a source by its reference, a tool answer by its tool and turn, a sibling
// by its kind and scenario, a control by its id). Nothing the editor can change is taken from Current unasked.
// ─────────────────────────────────────────────────────────────────────────────

export type MergeChoice = 'yours' | 'current' | 'manual';

type FieldValue = string | number | ScenarioEvent | ScenarioCheck | ScenarioSource | ScenarioToolDouble | ScenarioRelation | ScenarioControl | undefined;

export interface ScenarioMergeConflict {
  /**
   * `purpose`, `hazard`, `world.<field>`, `event:<turn>`, `check:<id>`, `source:<ref>`, `double:<tool>@<turn>`,
   * `relation:<kind>/<scenario>` or `control:<id>`.
   */
  key: string;
  label: string;
  /** Text fields (the purpose, what the user says) can be merged by hand. */
  manual: boolean;
  original: string;
  yours: string;
  current: string;
}

export interface ScenarioMerge {
  conflicts: ScenarioMergeConflict[];
  /** How many of your changed fields merged without a choice. */
  clean: number;
}

export interface MergeResolution {
  choice: MergeChoice;
  /** For a manual merge: the text you wrote. */
  text?: string;
}

const WORLD: WorldId[] = ['merchantId', 'memberId', 'memberContactId', 'infusionProtocolId'];
const WORLD_WORDS: Record<WorldId, string> = {
  merchantId: 'Practice',
  memberId: 'Patient',
  memberContactId: 'Who speaks',
  infusionProtocolId: 'Protocol',
};

/** JSON with keys sorted, so two equal values compare equal whatever their key order. */
export function stableJson(v: unknown): string {
  if (v === undefined) return '∅';
  return JSON.stringify(v, (_k, x) =>
    x && typeof x === 'object' && !Array.isArray(x) ? Object.fromEntries(Object.entries(x).sort(([a], [b]) => a.localeCompare(b))) : x);
}

// The key each listed item is merged by.
const sourceKey = (x: ScenarioSource) => `source:${x.ref}`;
const doubleKey = (x: ScenarioToolDouble) => `double:${x.tool.trim().toLowerCase()}@${x.turnIndex ?? '*'}`;
const relationKey = (x: ScenarioRelation) => `relation:${x.kind}/${x.scenarioId}`;
const controlKey = (x: ScenarioControl) => `control:${x.id}`;
const LISTED = ['check:', 'source:', 'double:', 'relation:', 'control:'];

function fields(s: EvaluationScenario): Map<string, FieldValue> {
  const m = new Map<string, FieldValue>();
  m.set('purpose', s.purpose ?? '');
  m.set('hazard', s.hazard ?? '');
  for (const w of WORLD) m.set(`world.${w}`, s.world?.[w] ?? 0);
  m.set('world.role', s.world?.role ?? '');
  for (const e of s.events ?? []) m.set(`event:${e.turnIndex}`, e);
  for (const c of s.checks ?? []) m.set(`check:${c.id}`, c);
  for (const x of s.sources ?? []) m.set(sourceKey(x), x);
  for (const x of s.toolDoubles ?? []) m.set(doubleKey(x), x);
  for (const x of s.relations ?? []) m.set(relationKey(x), x);
  for (const x of s.controls ?? []) m.set(controlKey(x), x);
  return m;
}

function show(key: string, v: FieldValue): string {
  if (v === undefined) return LISTED.some((p) => key.startsWith(p)) ? '(removed)' : '(none)';
  if (key.startsWith('event:')) return (v as ScenarioEvent).text ?? '';
  if (key.startsWith('check:')) return checkSentence(v as ScenarioCheck);
  if (key.startsWith('source:')) {
    const x = v as ScenarioSource;
    return [x.ref, x.section, x.revision, x.note].filter(Boolean).join(' · ');
  }
  if (key.startsWith('double:')) {
    const x = v as ScenarioToolDouble;
    return `${DOUBLE_OUTCOME_WORDS[x.outcome] ?? x.outcome}: ${x.response}`;
  }
  if (key.startsWith('relation:')) {
    const x = v as ScenarioRelation;
    return `${RELATION_WORDS[x.kind]?.label ?? x.kind} of ${x.scenarioId}${x.note ? ` · ${x.note}` : ''}`;
  }
  if (key.startsWith('control:')) {
    const x = v as ScenarioControl;
    return `${CONTROL_WORDS[x.kind]?.label ?? x.kind}, turn ${x.turnIndex + 1}: “${x.reply}”`;
  }
  if (key === 'world.role') return v ? `“${v}”` : 'none';
  if (key.startsWith('world.')) return v === 0 ? 'none' : `#${v}`;
  return String(v);
}

function label(key: string, any: FieldValue): string {
  if (key === 'purpose') return 'What this scenario tests';
  if (key === 'hazard') return 'Hazard';
  if (key === 'world.role') return 'Named world';
  if (key.startsWith('world.')) return WORLD_WORDS[key.slice(6) as WorldId];
  if (key.startsWith('event:')) return `Turn ${Number(key.slice(6)) + 1} · what the user says`;
  if (key.startsWith('source:')) return `Source ${key.slice(7)}`;
  if (key.startsWith('double:')) {
    const x = any as ScenarioToolDouble | undefined;
    return x ? `What ${x.tool} answers${x.turnIndex == null ? '' : ` on turn ${x.turnIndex + 1}`}` : 'A tool answer';
  }
  if (key.startsWith('relation:')) return 'A sibling';
  if (key.startsWith('control:')) return `Control ${key.slice(8)}`;
  return any ? `${checkPlace(any as ScenarioCheck)} · a check` : 'A check';
}

/** Text fields (the purpose, the hazard, what the user says) can be merged by hand. */
function manualOf(key: string, s: Side): boolean {
  return key === 'purpose' || key === 'hazard' || (key.startsWith('event:') && s.yours !== undefined && s.current !== undefined);
}

type Side = { original: FieldValue; yours: FieldValue; current: FieldValue };

function sides(original: EvaluationScenario, yours: EvaluationScenario, current: EvaluationScenario): Map<string, Side> {
  const o = fields(original), y = fields(yours), c = fields(current);
  const keys = new Set([...o.keys(), ...y.keys(), ...c.keys()]);
  return new Map([...keys].map((k) => [k, { original: o.get(k), yours: y.get(k), current: c.get(k) }]));
}

/** What merging Yours onto Current needs: the conflicts to choose, and how many of your changes merge on their own. */
export function mergeScenario(original: EvaluationScenario, yours: EvaluationScenario, current: EvaluationScenario): ScenarioMerge {
  const conflicts: ScenarioMergeConflict[] = [];
  let clean = 0;
  for (const [key, s] of sides(original, yours, current)) {
    const mine = stableJson(s.yours) !== stableJson(s.original);
    const theirs = stableJson(s.current) !== stableJson(s.original);
    if (!mine) continue;
    if (!theirs || stableJson(s.yours) === stableJson(s.current)) {
      clean++;
      continue;
    }
    conflicts.push({
      key,
      label: label(key, s.yours ?? s.current ?? s.original),
      manual: manualOf(key, s),
      original: show(key, s.original),
      yours: show(key, s.yours),
      current: show(key, s.current),
    });
  }
  return { conflicts, clean };
}

/**
 * The merged version: Current with your clean changes and your choices applied. Null while any conflict is unchosen, or
 * a manual merge is empty: saving is not offered until every conflict is decided.
 */
export function resolveMerge(
  original: EvaluationScenario,
  yours: EvaluationScenario,
  current: EvaluationScenario,
  choices: Record<string, MergeResolution | undefined>,
): EvaluationScenario | null {
  const pick = new Map<string, FieldValue>();
  for (const [key, s] of sides(original, yours, current)) {
    const mine = stableJson(s.yours) !== stableJson(s.original);
    const theirs = stableJson(s.current) !== stableJson(s.original);
    if (!mine) pick.set(key, s.current);
    else if (!theirs || stableJson(s.yours) === stableJson(s.current)) pick.set(key, s.yours);
    else {
      const r = choices[key];
      if (!r) return null;
      if (r.choice === 'yours') pick.set(key, s.yours);
      else if (r.choice === 'current') pick.set(key, s.current);
      else {
        if (!r.text?.trim()) return null;
        pick.set(key, key === 'purpose' || key === 'hazard' ? r.text : { ...(s.current as ScenarioEvent), text: r.text });
      }
    }
  }

  const world = { ...current.world };
  for (const w of WORLD) world[w] = Number(pick.get(`world.${w}`) ?? 0);
  const role = String(pick.get('world.role') ?? '');
  if (role) world.role = role;
  else delete world.role;
  const events = [...pick.entries()]
    .filter(([k, v]) => k.startsWith('event:') && v !== undefined)
    .map(([, v]) => v as ScenarioEvent)
    .sort((a, b) => a.turnIndex - b.turnIndex);
  // Listed items keep Current's order; items only you added follow, in your order.
  function merged<T>(of: (s: EvaluationScenario) => T[] | undefined, keyOf: (x: T) => string): T[] {
    const keys = [...(of(current) ?? []), ...(of(yours) ?? [])].map(keyOf);
    return keys
      .filter((k, i) => keys.indexOf(k) === i)
      .map((k) => pick.get(k) as T | undefined)
      .filter((x): x is T => x !== undefined);
  }
  const orNone = <T,>(list: T[]) => (list.length ? list : undefined);
  const hazard = String(pick.get('hazard') ?? '');

  return {
    ...current,
    purpose: String(pick.get('purpose') ?? ''),
    hazard: hazard || undefined,
    world,
    events,
    checks: merged((s) => s.checks, (c) => `check:${c.id}`),
    sources: orNone(merged((s) => s.sources, sourceKey)),
    toolDoubles: orNone(merged((s) => s.toolDoubles, doubleKey)),
    relations: orNone(merged((s) => s.relations, relationKey)),
    controls: orNone(merged((s) => s.controls, controlKey)),
  };
}
